import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
    DialogHeader,
    DialogFooter,
    DialogClose,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import EDatePicker from '@/components/everridge/EDatePicker';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { cn } from '@/lib/utils';
import {
    SuperAdminBookingsPageFilterFormData,
    SuperAdminBookingsPageFilterFormSchema,
} from '@/features/superAdmin/features/data/bookings/validators/SuperAdminBookingsPageFilterValidator';
import { SuperAdminDataListFilterState } from '@/features/superAdmin/features/data/types/superAdmin-data.types';
import SuperAdminTenantLookup from '@/features/superAdmin/features/data/shared/lookups/SuperAdminTenantLookup';
import SuperAdminHotelLookup from '@/features/superAdmin/features/data/shared/lookups/SuperAdminHotelLookup';
import SuperAdminOwnerLookup from '@/features/superAdmin/features/data/shared/lookups/SuperAdminOwnerLookup';
import SuperAdminRoomLookup from '@/features/superAdmin/features/data/shared/lookups/SuperAdminRoomLookup';

type Props = {
    onClose: () => void;
    isMobile?: boolean;
    filter: SuperAdminDataListFilterState;
    setFilter: (filter: Partial<SuperAdminDataListFilterState>) => void;
    resetFilter: () => void;
};

const toSelection = (id: string | null | undefined, name?: string | null) =>
    id ? [{ id, name: name ?? id }] : [];

const isNestedPickerTarget = (target: EventTarget | null) => {
    const element = target as HTMLElement | null;
    return Boolean(
        element?.closest('[data-radix-popper-content-wrapper]') ||
            element?.closest('[role="menu"]'),
    );
};

const SuperAdminBookingsPageFilter: FC<Props> = ({
    onClose,
    isMobile,
    filter,
    setFilter,
    resetFilter,
}) => {
    const [isOpen, setIsOpen] = useState(true);
    const { t } = useTranslation();
    const skipCascadeRef = useRef(true);
    const previousTenantIdRef = useRef<string | null>(filter.tenantId);
    const previousHotelIdRef = useRef<string | null>(filter.hotelId);

    const { control, handleSubmit, reset, setValue, register } =
        useForm<SuperAdminBookingsPageFilterFormData>({
            resolver: zodResolver(SuperAdminBookingsPageFilterFormSchema),
            values: {
                startDate: filter.startDate,
                endDate: filter.endDate,
                tenant: toSelection(filter.tenantId, filter.tenantName),
                hotel: toSelection(filter.hotelId, filter.hotelName),
                owner: toSelection(filter.ownerId, filter.ownerName),
                apartment: toSelection(filter.apartmentId, filter.apartmentName),
                documentNumber: filter.documentNumber,
            },
        });

    const tenantValue = useWatch({ control, name: 'tenant' });
    const hotelValue = useWatch({ control, name: 'hotel' });
    const tenantId = tenantValue?.[0]?.id ?? null;
    const hotelId = hotelValue?.[0]?.id ?? null;

    useEffect(() => {
        if (skipCascadeRef.current) {
            skipCascadeRef.current = false;
            previousTenantIdRef.current = tenantId;
            previousHotelIdRef.current = hotelId;
            return;
        }

        if (previousTenantIdRef.current !== tenantId) {
            setValue('hotel', []);
            setValue('owner', []);
            setValue('apartment', []);
            previousTenantIdRef.current = tenantId;
            previousHotelIdRef.current = null;
        }
    }, [tenantId, setValue]);

    useEffect(() => {
        if (skipCascadeRef.current) return;

        if (previousHotelIdRef.current !== hotelId) {
            setValue('apartment', []);
            previousHotelIdRef.current = hotelId;
        }
    }, [hotelId, setValue]);

    const handleClose = () => {
        setIsOpen(false);
        setTimeout(onClose, 300);
    };

    const onSubmit = (data: SuperAdminBookingsPageFilterFormData) => {
        const tenant = data.tenant[0];
        const hotel = data.hotel[0];
        const owner = data.owner[0];
        const apartment = data.apartment[0];

        setFilter({
            startDate: data.startDate,
            endDate: data.endDate,
            tenantId: tenant?.id ?? null,
            tenantName: tenant?.name ?? null,
            hotelId: hotel?.id ?? null,
            hotelName: hotel?.name ?? null,
            ownerId: owner?.id ?? null,
            ownerName: owner?.name ?? null,
            apartmentId: apartment?.id ?? null,
            apartmentName: apartment?.name ?? null,
            documentNumber: data.documentNumber?.trim() || null,
            skip: 0,
        });
        handleClose();
    };

    const handleReset = () => {
        resetFilter();
        reset({
            startDate: null,
            endDate: null,
            tenant: [],
            hotel: [],
            owner: [],
            apartment: [],
            documentNumber: null,
        });
    };

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(open) => {
                if (!open) handleClose();
            }}
        >
            <DialogContent
                className={cn(isMobile && 'max-h-[90vh] overflow-y-auto')}
                onInteractOutside={(event) => {
                    if (isNestedPickerTarget(event.target)) {
                        event.preventDefault();
                    }
                }}
                onPointerDownOutside={(event) => {
                    if (isNestedPickerTarget(event.target)) {
                        event.preventDefault();
                    }
                }}
            >
                <DialogHeader>
                    <DialogTitle>{t('superAdmin.data.filters.title')}</DialogTitle>
                    <DialogDescription>{t('superAdmin.data.filters.description')}</DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                    <SuperAdminTenantLookup
                        control={control}
                        fieldName="tenant"
                        label={t('superAdmin.data.filters.tenant')}
                        placeholder={t('superAdmin.data.filters.tenantPlaceholder')}
                        selectVariant="single"
                        addResetButton
                    />

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <EDatePicker
                            control={control}
                            fieldName="startDate"
                            label={t('superAdmin.data.filters.startDate')}
                        />
                        <EDatePicker
                            control={control}
                            fieldName="endDate"
                            label={t('superAdmin.data.filters.endDate')}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="documentNumber">
                            {t('superAdmin.data.filters.documentNumber')}
                        </Label>
                        <Input id="documentNumber" {...register('documentNumber')} />
                    </div>

                    <SuperAdminHotelLookup
                        control={control}
                        fieldName="hotel"
                        tenantId={tenantId}
                        label={t('superAdmin.data.filters.hotel')}
                        placeholder={t('superAdmin.data.filters.hotelPlaceholder')}
                        selectVariant="single"
                        addResetButton
                    />

                    <SuperAdminOwnerLookup
                        control={control}
                        fieldName="owner"
                        tenantId={tenantId}
                        label={t('superAdmin.data.filters.owner')}
                        placeholder={t('superAdmin.data.filters.ownerPlaceholder')}
                        selectVariant="single"
                        addResetButton
                    />

                    <SuperAdminRoomLookup
                        control={control}
                        fieldName="apartment"
                        tenantId={tenantId}
                        hotelId={hotelId}
                        label={t('superAdmin.data.filters.room')}
                        placeholder={t('superAdmin.data.filters.roomPlaceholder')}
                        selectVariant="single"
                        addResetButton
                    />

                    <DialogFooter className="flex-wrap justify-end gap-2">
                        <Button type="button" variant="outline" onClick={handleReset}>
                            {t('elements.buttons.reset')}
                        </Button>
                        <DialogClose asChild>
                            <Button type="button" variant="outline" className="gap-2">
                                <Cancel_01_Icon className="h-4 w-4" />
                                {t('elements.buttons.cancel')}
                            </Button>
                        </DialogClose>
                        <Button type="submit">{t('elements.buttons.apply')}</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default SuperAdminBookingsPageFilter;
